import { useRef } from 'react';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, FileText, ExternalLink, Upload, X } from 'lucide-react';

interface CertificateStepProps {
    expiresAt: string;
    setExpiresAt: (value: string) => void;
    certificateFile: File | null;
    setCertificateFile: (file: File | null) => void;
    existingCertificateUrl: string | null;
}

export default function CertificateStep({
    expiresAt,
    setExpiresAt,
    certificateFile,
    setCertificateFile,
    existingCertificateUrl,
}: CertificateStepProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setCertificateFile(file);
        }
    };

    const handleClearFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCertificateFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-8">
            {/* Expiry Date */}
            <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Expiry Date *
                </Label>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            className={cn(
                                "w-full h-12 justify-start text-left font-normal",
                                !expiresAt && "text-muted-foreground"
                            )}
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {expiresAt ? format(new Date(expiresAt), "PPP") : <span>Pick a date</span>}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={expiresAt ? new Date(expiresAt) : undefined}
                            onSelect={(date) => date && setExpiresAt(format(date, "yyyy-MM-dd"))}
                            initialFocus
                        />
                    </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                    Date the roadworthiness certificate expires
                </p>
            </div>

            {/* Certificate Upload */}
            <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Roadworthiness Certificate
                </Label>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                />

                <div
                    onClick={handleFileClick}
                    className="relative group cursor-pointer border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 hover:bg-muted/30 transition-all"
                >
                    {certificateFile ? (
                        <div className="flex items-center justify-center gap-3">
                            <FileText className="h-8 w-8 text-primary" />
                            <div className="text-left">
                                <p className="text-sm font-medium">{certificateFile.name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {(certificateFile.size / 1024).toFixed(1)} KB
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 ml-2"
                                onClick={handleClearFile}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="h-12 w-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                                <Upload className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <p className="text-sm font-medium">Upload certificate</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                PDF documents up to 5MB
                            </p>
                        </>
                    )}
                </div>

                {existingCertificateUrl && !certificateFile && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => openSignedUrl(existingCertificateUrl!)}
                    >
                        <FileText className="h-4 w-4 mr-2" />
                        View current document
                        <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                )}
            </div>
        </div>
    );
}
